import React, { useEffect, useState } from 'react';
import './honestWeekStyle.css';
import { v4 as uuidv4 } from 'uuid';
import { ArrowLeft } from './ArrowLeft';
import { ArrowRight } from './ArrowRight';
import { addMonths, endOfWeek, startOfWeek, subMonths, getDaysInMonth } from 'date-fns';
import moment from 'moment';
import { useTranslation } from 'helpers/languages/translations';

type WeekRange = {
    firstDay: Date;
    lastDay: Date;
};

type HonestWeekPickerProps = {
    onChange?: (week: WeekRange) => void;
    dateInWeeks: {
        week?: number;
        year?: number;
    };
    setDateInWeeks: React.Dispatch<React.SetStateAction<{ week?: number; year?: number }>>;
    style?: React.CSSProperties;
    textStyle?: React.CSSProperties;
};

export const WeeklyCalendar: React.FC<HonestWeekPickerProps> = ({
    onChange,
    dateInWeeks,
    setDateInWeeks,
    style = {},
    textStyle = {},
}) => {
    const [open, setOpen] = useState<boolean>(false);
    const [date, setDate] = useState<Date>(new Date());
    const [displayDate, setDisplayDate] = useState<Date>(new Date());
    const [week, setWeek] = useState<WeekRange>({
        firstDay: startOfWeek(new Date(), { weekStartsOn: 1 }),
        lastDay: endOfWeek(new Date(), { weekStartsOn: 1 }),
    });
    const translate = useTranslation();

    useEffect(() => {
        const weekNum = moment(date).isoWeek();
        const year = date.getFullYear();
        setDateInWeeks({ week: weekNum, year });
    }, [date]);

    useEffect(() => {
        onChange && onChange(week);
    }, [week, onChange]);

    const isLeapYear = (): boolean => {
        const leapYear = new Date(new Date().getFullYear(), 1, 29);
        return leapYear.getDate() === 29;
    };

    const convertDate = (value: Date): string => {
        const dt = new Date(value);
        return `${dt.getDate()}.${dt.getMonth() + 1}.${dt.getFullYear()}.`;
    };

    const handleClick = (e: React.MouseEvent<HTMLDivElement>): void => {
        const target = e.target as HTMLElement;
        const id = target?.id ?? '';
        let localDate: Date;

        if (id.includes('prev')) {
            localDate = new Date(displayDate);
            localDate.setDate(1);
        } else if (id.includes('next')) {
            localDate = new Date(displayDate);
            localDate.setDate(getDaysInMonth(localDate));
        } else {
            const day = Number(id);
            localDate = new Date(displayDate);
            localDate.setDate(day);
        }

        setDate(new Date(localDate));
        setDisplayDate(new Date(localDate));

        const firstDay = startOfWeek(localDate, { weekStartsOn: 1 });
        const lastDay = endOfWeek(localDate, { weekStartsOn: 1 });
        setWeek({ firstDay, lastDay });
    };

    const months: string[] = [
        'Jan.',
        'Feb.',
        'Mar.',
        'Apr.',
        'May',
        'Jun',
        'July',
        'Aug.',
        'Sep.',
        'Oct.',
        'Nov.',
        'Dec.',
    ];

    const days: Record<number, number> = {
        1: 31,
        2: isLeapYear() ? 29 : 28,
        3: 31,
        4: 30,
        5: 31,
        6: 30,
        7: 31,
        8: 31,
        9: 30,
        10: 31,
        11: 30,
        12: 31,
    };

    const renderDays = (): React.JSX.Element[] => {
        const month = displayDate.getMonth() + 1; // 1..12
        const ar: React.JSX.Element[] = [];
        const endOfCurrentWeek = endOfWeek(new Date(), { weekStartsOn: 1 });

        const isDateAfterCurrentWeek = (value: Date): boolean => {
            return value.getTime() > endOfCurrentWeek.getTime();
        };

        for (let i = 1; i <= days[month]; i++) {
            const currentDate = new Date(displayDate);
            currentDate.setDate(i);
            const firstDayDate = new Date(week.firstDay);
            const lastDayDate = new Date(week.lastDay);

            let cName = 'single-number ';
            if (firstDayDate.getTime() <= currentDate.getTime() && currentDate.getTime() <= lastDayDate.getTime()) {
                cName += 'selected-week';
            }

            const isDisabled = isDateAfterCurrentWeek(currentDate);
            if (isDisabled) {
                cName += ' disabled';
            }

            ar.push(
                <div key={uuidv4()} id={String(i)} className={cName} onClick={isDisabled ? undefined : handleClick}>
                    {i}
                </div>,
            );
        }

        const firstDayInDisplayedMonth = new Date(displayDate);
        firstDayInDisplayedMonth.setDate(1);
        let dayInTheWeek = firstDayInDisplayedMonth.getDay();
        if (dayInTheWeek < 1) {
            dayInTheWeek = 7;
        }

        const prevMonth: React.JSX.Element[] = [];
        let prevMonthDays: number = new Date(displayDate).getMonth();
        if (prevMonthDays === 0) {
            prevMonthDays = 12;
        }

        for (let i = dayInTheWeek; i > 1; i--) {
            const previousMonth = new Date(displayDate);
            previousMonth.setMonth(previousMonth.getMonth() - 1);

            const currentDate = new Date(previousMonth);
            currentDate.setDate(days[prevMonthDays] - i + 2);

            let cName = 'single-number other-month';
            const currentTime = currentDate.getTime();
            const firstTime = new Date(week.firstDay).getTime();
            const endTime = new Date(week.lastDay).getTime();
            if (currentTime >= firstTime && currentTime <= endTime) {
                cName = 'single-number selected-week';
            }

            const isDisabled = isDateAfterCurrentWeek(currentDate);
            if (isDisabled) {
                cName += ' disabled';
            }

            const dayInCalendar = days[prevMonthDays] - i + 2;
            prevMonth.push(
                <div onClick={isDisabled ? undefined : handleClick} key={uuidv4()} id={`prev-${i}`} className={cName}>
                    {dayInCalendar}
                </div>,
            );
        }

        const nextMonth: React.JSX.Element[] = [];
        let fullDays = 35;
        if ([...prevMonth, ...ar].length > 35) {
            fullDays = 42;
        }

        for (let i = 1; i <= fullDays - [...prevMonth, ...ar].length; i++) {
            let cName = 'single-number other-month';

            const currentDate = new Date(displayDate);
            currentDate.setMonth(currentDate.getMonth() + 1);
            currentDate.setDate(i);

            const isDisabled = isDateAfterCurrentWeek(currentDate);
            if (isDisabled) {
                cName += ' disabled';
            }

            nextMonth.push(
                <div onClick={isDisabled ? undefined : handleClick} key={uuidv4()} id={`next-${i}`} className={cName}>
                    {i}
                </div>,
            );
        }

        const fullMonthCalendar = [...prevMonth, ...ar, ...nextMonth];
        return fullMonthCalendar;
    };

    const handleDate = (next?: boolean): void => {
        let localDate = new Date(displayDate);
        if (next) {
            localDate = addMonths(localDate, 1);
        } else {
            localDate = subMonths(localDate, 1);
        }
        setDisplayDate(new Date(localDate));
    };

    return (
        <div
            className="week-picker-display"
            onBlur={() => setOpen(false)}
            tabIndex={0}
            style={style}
            // on press esc it should close the calendar
            onKeyDown={(e) => {
                if (e.key === 'Escape') {
                    setOpen(false);
                }
            }}
        >
            <p
                className="week-picker-display-text"
                onClick={() => setOpen(!open)}
                style={{
                    width: '100%',
                    height: '100%',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 1rem',
                    ...textStyle,
                }}
            >
                {translate('calendar.weekly.display_date', {
                    week: dateInWeeks.week,
                    year: dateInWeeks.year,
                })}
            </p>
            {open && (
                <div className="week-picker-options">
                    <div className="title-week">
                        <div onClick={() => handleDate()} className="arrow-container">
                            {ArrowLeft}
                        </div>
                        {`${translate(
                            'month.' + months[displayDate.getMonth()].toLowerCase().replace('.', ''),
                        )}  ${displayDate.getFullYear()}.`}
                        <div onClick={() => handleDate(true)} className="arrow-container">
                            {ArrowRight}
                        </div>
                    </div>
                    <div className="numbers-container">
                        <div className="single-number day">{translate('calendar.weekly.monday')}</div>
                        <div className="single-number day">{translate('calendar.weekly.tuesday')}</div>
                        <div className="single-number day">{translate('calendar.weekly.wednesday')}</div>
                        <div className="single-number day">{translate('calendar.weekly.thursday')}</div>
                        <div className="single-number day">{translate('calendar.weekly.friday')}</div>
                        <div className="single-number day">{translate('calendar.weekly.saturday')}</div>
                        <div className="single-number day">{translate('calendar.weekly.sunday')}</div>
                    </div>
                    <div className="numbers-container">{renderDays()}</div>
                </div>
            )}
        </div>
    );
};
